<template>
    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
        <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
            <h3 class="text-lg text-gray-700 font-bold">Treatments</h3>
            <USelectMenu v-if="canWrite" v-model="adding" :options="treatments" value-attribute="id" option-attribute="label" searchable
                searchable-placeholder="Search treatments..." :popper="{ placement: 'bottom-end' }" :ui-menu="{ width: 'w-72' }"
                @change="(v: string) => add(v)">
                <UButton label="Add" icon="i-ph-plus-bold" color="black" size="sm" />
            </USelectMenu>
        </div>

        <div v-if="!rows.length" class="flex flex-col items-center justify-center space-y-1 p-6">
            <UIcon name="i-ph-first-aid-kit-duotone" class="size-5 text-gray-500" />
            <span class="text-[13px] italic text-gray-500">No treatments.</span>
        </div>

        <div v-else class="divide-y divide-dashed divide-gray-300">
            <div v-for="row in rows" :key="row.id" class="flex items-start gap-4 px-6 py-5">
                <div class="grid flex-1 grid-cols-6 gap-4">
                    <UFormGroup label="Treatment" class="col-span-4">
                        <USelectMenu v-model="row.treatment_id" :options="treatments" value-attribute="id" option-attribute="label" searchable
                            :disabled="!canWrite" @change="(v: string) => update(row, { treatment_id: v })" />
                    </UFormGroup>
                    <UFormGroup label="Status" class="col-span-2">
                        <USelectMenu v-model="row.status" :options="TREATMENT_STATUS_OPTIONS" value-attribute="value" option-attribute="label"
                            :disabled="!canWrite" @change="(v: string) => update(row, { status: v })" />
                    </UFormGroup>
                    <UFormGroup label="Given" class="col-span-2">
                        <UInput v-model="row.given_local" type="datetime-local" :disabled="!canWrite"
                            @blur="saveGivenAt(row)" />
                    </UFormGroup>
                    <UFormGroup label="Physician" class="col-span-2">
                        <USelectMenu v-model="row.physician_id" :options="team" value-attribute="id" option-attribute="name" searchable
                            placeholder="Select..." :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pickMember(row, 'physician_id', v)">
                            <template v-if="canWrite && row.physician_id" #trailing>
                                <button type="button" class="flex items-center justify-center" @click.stop="row.physician_id = null; pickMember(row, 'physician_id', null)">
                                    <UIcon name="i-ph-x-bold" class="size-3" />
                                </button>
                            </template>
                        </USelectMenu>
                    </UFormGroup>
                    <UFormGroup label="Nurse" class="col-span-2">
                        <USelectMenu v-model="row.nurse_id" :options="team" value-attribute="id" option-attribute="name" searchable
                            placeholder="Select..." :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pickMember(row, 'nurse_id', v)">
                            <template v-if="canWrite && row.nurse_id" #trailing>
                                <button type="button" class="flex items-center justify-center" @click.stop="row.nurse_id = null; pickMember(row, 'nurse_id', null)">
                                    <UIcon name="i-ph-x-bold" class="size-3" />
                                </button>
                            </template>
                        </USelectMenu>
                    </UFormGroup>
                </div>
                <UDropdown v-if="canWrite" :items="rowActions(row)" :popper="{ placement: 'bottom-end' }" class="mt-6">
                    <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                </UDropdown>
            </div>
        </div>

        <OverlayFieldPostRemoveRow v-model:open="removal.open" title="Remove Treatment"
            description="Are you sure you want to remove this treatment from the intervention?" :loading="removal.loading"
            @confirm="remove()" />
    </div>
</template>

<script setup lang="ts">
import type { Option } from '~/types/option'
import { TREATMENT_STATUS_OPTIONS, TreatmentStatus, type InterventionTreatment, type TeamMember } from '~/types/fieldPost'
import { fromLocalInput, toLocalInput } from '~/utils/fieldPosts'

type Row = InterventionTreatment & { given_local: string }

const props = defineProps<{
    interventionId: string
    treatments: Option[]
    team: TeamMember[]
    canWrite: boolean
}>()

const client = useSupabaseClient()
const toast = useToast()

const CLEARABLE_UI = { icon: { trailing: { pointer: 'pointer-events-auto' } } }

const rows = ref<Row[]>([])
const adding = ref<string | null>(null)
const removal = reactive({ open: false, loading: false, row: null as Row | null })

function rowActions(row: Row) {
    return [[{
        label: 'Remove',
        icon: 'i-ph-trash-fill',
        iconClass: 'text-red-400',
        click: () => Object.assign(removal, { open: true, row }),
    }]]
}

onMounted(() => fetchRows())

async function fetchRows() {
    const { data } = await client.from('intervention_treatments').select('*').eq('intervention_id', props.interventionId).order('given_at')
    rows.value = ((data ?? []) as InterventionTreatment[]).map(t => ({ ...t, given_local: toLocalInput(t.given_at) }))
}

function saveGivenAt(row: Row) {
    const givenAt = fromLocalInput(row.given_local)
    if (!givenAt) {
        row.given_local = toLocalInput(row.given_at)
        return
    }
    update(row, { given_at: givenAt })
}

async function add(treatmentId: string) {
    adding.value = null
    const { error } = await client.from('intervention_treatments').insert({
        intervention_id: props.interventionId,
        treatment_id: treatmentId,
        status: TreatmentStatus.administered,
    } as never)
    if (error) toast.add({ title: 'Could not add the treatment.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
    await fetchRows()
}

function pickMember(row: Row, key: 'physician_id' | 'nurse_id', value: string | null) {
    update(row, { [key]: value ?? null })
}

async function update(row: Row, patch: Record<string, unknown>) {
    const { error } = await client.from('intervention_treatments').update(patch as never).eq('id', row.id)
    if (error) {
        toast.add({ title: 'Treatment not saved.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        await fetchRows()
    }
}

async function remove() {
    if (!removal.row) return
    removal.loading = true
    const { error } = await client.from('intervention_treatments').delete().eq('id', removal.row.id)
    removal.loading = false
    if (error) toast.add({ title: 'Could not remove the treatment.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
    else removal.open = false
    await fetchRows()
}
</script>
